import useSWR from 'swr';
import { Draft, produce } from 'immer';
import { API_URLS, SWR_REQUEST_DEFAULTS } from '@/lib/api/config';
import {
  TypeMediaCollection,
  TypePlaybackHistoriesResponseData,
  TypePlaybackHistoryRecord,
  TypePlaybackHistoryResponseSuccess,
} from '@/lib/api/playbackService/types';
import {
  createPlaybackHistory,
  updatePlaybackHistory,
} from '@/lib/api/playbackService/api';

export type TypePlaybackHistoryMutations = {
  create: (mediaAssetId: string, onError?: () => void) => void;
  update: (
    playbackHistoryId: string,
    head: number,
    completed_at?: Date,
    onError?: () => void,
  ) => void;
};

export default function useMediaCollectionPlaybackHistories(
  anchor: string,
  type: TypeMediaCollection,
) {
  const { data, error, mutate } = useSWR<
    TypePlaybackHistoriesResponseData,
    Error
  >(
    `${API_URLS.PLAYBACK_SERVICE_PROXY}/media_collections/${type}/${anchor}/playback_histories`,
    { ...SWR_REQUEST_DEFAULTS },
  );

  async function create(mediaAssetId: string, onError?: () => void) {
    const response = await createPlaybackHistory(mediaAssetId);
    const { ok } = response;

    if (!ok) {
      if (onError) {
        onError();
      }
      return;
    }

    const responseSuccess = response as TypePlaybackHistoryResponseSuccess;
    handlePlaybackHistoryMutation(responseSuccess.data.playback_history);
  }

  async function update(
    playbackHistoryId: string,
    head: number,
    completed_at?: Date,
    onError?: () => void,
  ) {
    const response = await updatePlaybackHistory(
      playbackHistoryId,
      head,
      completed_at,
    );
    const { ok } = response;

    if (!ok) {
      if (onError) {
        onError();
      }
      return;
    }

    const responseSuccess = response as TypePlaybackHistoryResponseSuccess;
    handlePlaybackHistoryMutation(responseSuccess.data.playback_history);
  }

  function applyMutation(newData: TypePlaybackHistoriesResponseData) {
    const options = {
      optimisticData: newData,
      rollbackOnError: true,
      revalidate: false,
    };

    mutate(newData, options);
  }

  // playback histories will be updated in 10 second intervals as the user streams videos and songs. In order to prevent
  // an unnecessary refetch to get all the playback histories when an update occurs, we can utilize optimistic data to maintain
  // our local state without having to revalidate regularly with the playback histories api.
  function handlePlaybackHistoryMutation(
    updatedPlaybackHistoryRecord: TypePlaybackHistoryRecord,
  ) {
    const newData = produce(
      data,
      (draft: Draft<TypePlaybackHistoriesResponseData>) => {
        const updatedPlaybackHistoryIdx = draft?.playback_histories?.findIndex(
          (playback_history) =>
            playback_history.id === updatedPlaybackHistoryRecord.id,
        );

        const playbackHistoryRecordFound = updatedPlaybackHistoryIdx !== -1;
        if (playbackHistoryRecordFound) {
          draft.playback_histories[updatedPlaybackHistoryIdx] =
            updatedPlaybackHistoryRecord;
        } else {
          draft.playback_histories.push(updatedPlaybackHistoryRecord);
        }
      },
    );
    const newPlaybackHistoriesData =
      newData as TypePlaybackHistoriesResponseData;
    applyMutation(newPlaybackHistoriesData);
  }

  return {
    data,
    loading: !error && !data,
    error,
    mutate,
    mutations: { create, update } as TypePlaybackHistoryMutations,
  };
}
